import React, { useEffect, useMemo, useState } from "react";
import StudentLeftside from "./StudentLeftside";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import API_BASE_URL from "../config/api";
import { Bar } from "react-chartjs-2";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import "./studentdashboard.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const DASHBOARD_ENDPOINT = `${API_BASE_URL}/dashboard_summary.php`;

const safeNumber = (v) =>
  typeof v === "number" && !isNaN(v) ? v : parseInt(v) || 0;

export default function OverallPerformance() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [masterAttempts, setMasterAttempts] = useState([]);
  const [bySubject, setBySubject] = useState({});
  const [subjectList, setSubjectList] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");

  const [overall, setOverall] = useState({
    correct: 0,
    incorrect: 0,
    totalQuestions: 0,
    accuracy: 0,
  });

  /* ================= FETCH ================= */
  useEffect(() => {
    const user = JSON.parse(sessionStorage.getItem("user"));
    if (!user?.id) {
      Swal.fire("Login Required", "Please login again", "warning").then(() => {
        window.location.href = "/login";
      });
      return;
    }

    fetch(`${DASHBOARD_ENDPOINT}?user_id=${user.id}`)
      .then((res) => res.json())
      .then((data) => {
        const masters = data.masterAttempts || [];
        const bySub = data.bySubject || {};

        setMasterAttempts(masters);
        setBySubject(bySub);

        const subs = Object.keys(bySub);
        setSubjectList(subs);
        setSelectedSubject(subs[0] || "");

        let masterCorrect = 0;
        let masterIncorrect = 0;
        let chapterCorrect = 0;
        let chapterIncorrect = 0;

        masters.forEach((m) => {
          masterCorrect += safeNumber(m.correct);
          masterIncorrect += safeNumber(m.incorrect);
        });

        Object.values(bySub).forEach((chapters) => {
          chapters.forEach((c) => {
            chapterCorrect += safeNumber(c.correct);
            chapterIncorrect += safeNumber(c.incorrect);
          });
        });

        const correct = masterCorrect + chapterCorrect;
        const incorrect = masterIncorrect + chapterIncorrect;
        const totalQuestions = correct + incorrect;
        const accuracy =
          totalQuestions > 0
            ? Math.round((correct / totalQuestions) * 100)
            : 0;

        setOverall({ correct, incorrect, totalQuestions, accuracy });
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load performance data");
        setLoading(false);
      });
  }, []);

  /* ================= DERIVED ================= */
  const currentChapters = useMemo(
    () => bySubject[selectedSubject] || [],
    [bySubject, selectedSubject]
  );

  const masterSummary = useMemo(() => {
    const correct = masterAttempts.reduce(
      (s, m) => s + safeNumber(m.correct),
      0
    );
    const incorrect = masterAttempts.reduce(
      (s, m) => s + safeNumber(m.incorrect),
      0
    );
    return { correct, incorrect };
  }, [masterAttempts]);

  const chapterSummary = useMemo(() => {
    const correct = currentChapters.reduce(
      (s, c) => s + safeNumber(c.correct),
      0
    );
    const incorrect = currentChapters.reduce(
      (s, c) => s + safeNumber(c.incorrect),
      0
    );
    return { correct, incorrect };
  }, [currentChapters]);

  /* ================= ATTEMPTS (QUIZ COUNT) ================= */
  const masterAttemptCount = masterAttempts.length;

  const chapterAttemptCount = Object.values(bySubject).reduce(
    (sum, chapters) => sum + chapters.length,
    0
  );

  const overallAttemptCount =
    masterAttemptCount + chapterAttemptCount;

  /* ================= ACCURACY PERCENTAGES ================= */
  const masterTotalQuestions =
    masterSummary.correct + masterSummary.incorrect || 1;

  const masterPercent = Math.round(
    (masterSummary.correct / masterTotalQuestions) * 100
  );

  const chapterTotalQuestions =
    chapterSummary.correct + chapterSummary.incorrect || 1;

  const chapterPercent = Math.round(
    (chapterSummary.correct / chapterTotalQuestions) * 100
  );

  const overallPercent = overall.accuracy;

  /* ================= CHART DATA ================= */
  const masterChartData = {
    labels: masterAttempts.map((m) => m.subject),
    datasets: [
      {
        label: "Correct",
        data: masterAttempts.map((m) => safeNumber(m.correct)),
        backgroundColor: "#28a745",
      },
      {
        label: "Incorrect",
        data: masterAttempts.map((m) => safeNumber(m.incorrect)),
        backgroundColor: "#dc3545",
      },
    ],
  };

  const chapterChartData = {
    labels: currentChapters.map((c) => c.title),
    datasets: [
      {
        label: "Correct",
        data: currentChapters.map((c) => safeNumber(c.correct)),
        backgroundColor: "#28a745",
      },
      {
        label: "Incorrect",
        data: currentChapters.map((c) => safeNumber(c.incorrect)),
        backgroundColor: "#dc3545",
      },
    ],
  };

  /* ================= STAT CARD ================= */
  function DashboardStatCard({ value, label, percent, icon, theme }) {
    const radius = 22;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (percent / 100) * circumference;

    return (
      <div className={`stat-card ${theme}`}>
        <div className="stat-left">
          <div className="stat-icon">
            <i className={icon}></i>
          </div>
          <div>
            <h4>{value}</h4>
            <p>{label}</p>
          </div>
        </div>

        <div className="stat-right">
          <svg width="56" height="56">
            <circle
              cx="28"
              cy="28"
              r={radius}
              stroke="#e9ecef"
              strokeWidth="6"
              fill="none"
            />
            <circle
              cx="28"
              cy="28"
              r={radius}
              stroke="currentColor"
              strokeWidth="6"
              fill="none"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              strokeLinecap="round"
            />
          </svg>
          <span className="stat-percent">{percent}%</span>
        </div>
      </div>
    );
  }

  if (loading) return <div className="p-5">Loading...</div>;

  return (
    <div className="dashboard-wrap bg-light">
      <StudentLeftside />

      <div className="dashboard-content container-fluid py-4 pt-5">
        <nav aria-label="breadcrumb" className="mb-4">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/studentdashboard">Dashboard</Link>
            </li>
            <li className="breadcrumb-item active">
              Overall Performance
            </li>
          </ol>
        </nav>

        <h3 className="mb-4 text-start">Overall Performance</h3>
        {error && <div className="alert alert-warning">{error}</div>}

        {/* ===== SUMMARY ===== */}
        <div className="row g-4 mb-5">
          <div className="col-md-4">
            <DashboardStatCard
              value={masterAttemptCount}
              label="Master Quizzes"
              percent={masterPercent}
              icon="fa-solid fa-layer-group"
              theme="stat-orange"
            />
          </div>

          <div className="col-md-4">
            <DashboardStatCard
              value={chapterAttemptCount}
              label="Chapter Quizzes"
              percent={chapterPercent}
              icon="fa-solid fa-book-open"
              theme="stat-pink"
            />
          </div>

          <div className="col-md-4">
            <DashboardStatCard
              value={overallAttemptCount}
              label="Overall Attempts"
              percent={overallPercent} 
              icon="fa-solid fa-book-open"

              theme="stat-blue"
            />
          </div>
        </div>
<div
  className="col-12 d-flex justify-content-start align-items-center"
  style={{
    marginTop: "-10px",
    marginBottom: "10px",
    paddingBottom: "0px"
  }}
>
                  <Link
                    to="/subjects"
                    className="btn btn-primary fw-semibold w-100 py-3 d-flex align-items-center justify-content-center gap-2"
                    style={{
                      maxWidth: "320px",
                      fontSize: "18px",
                      textTransform: "uppercase",
                    }}
                  >
                    <i className="fa-solid fa-play"></i>
                    START TEST
                  </Link>
                </div>
        {/* ===== CHARTS ===== */}
        <div className="row g-4">
          <div className="col-lg-6">
            <div className="card shadow-sm h-100">
              <div className="card-header bg-white text-center">
                <h5>Master Summary</h5>
              </div>
              <div className="card-body">
                <Bar data={masterChartData} />
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="card shadow-sm h-100">
              <div className="card-header bg-white d-flex justify-content-between align-items-center">
                <h5>Chapter-wise Summary</h5>
                <select
                  className="form-select w-auto"
                  value={selectedSubject}
                  onChange={(e) => setSelectedSubject(e.target.value)}
                >
                  {subjectList.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="card-body">
                <Bar data={chapterChartData} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
